import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ICancelOrderPayload, IImportOrderPayload, IOrderEntity, IOrdersListPayload, IPickingFlowResponse, OrderStatusEnum } from "../../../Services/api/orders/orders.interface";
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase";
import { ListProductUseCase } from "../../../Usecase/Products/list-product-usecase";
import { ReprocessOrderPaymentsUseCase } from "../../../Usecase/OrderPayments/reprocess-order-payments-usecase";
import IKanbanBoardOrdersSchemaProps from "../../../Components/KanbanBoardOrders/interfaces/kanban-board-orders-schema.interface";
import { Task } from "../../../Components/KanbanBoardOrders/kanban-board-orders.component";
import { IOrderData } from "../../../Components/Modals/variants/order-details/order-details-modal.component";
import { PickingFlowUseCase } from "../../../Usecase/Orders/picking-flow.usecase";
import { UpdateOrdersUseCase } from "../../../Usecase/Orders/update-order-usecase";
import { ExportInPickingOrderUseCase } from "../../../Usecase/Orders/export-in-picking-order.usecase";
import { ISaleEntity } from "../../Financial/Details/financial-details.hook";
import { ShowOrdersUseCase } from "../../../Usecase/Orders/show-order-usecase";
import { useAuth } from "../../../Hooks/Auth";
import { CancelOrderUseCase } from "../../../Usecase/Orders/cancel-order-usecase";
import { ImportOrderUseCase } from "../../../Usecase/Orders/import-order-usecase";
import { TStoreType } from "../../../Services/api/service-orders/service-order.interface";
import { GridSortModel } from "@mui/x-data-grid";
import { FormatMoney } from "../../../utils/formatMoney";


interface IUseListOrdersProps {
    listProductUseCase: ListProductUseCase
    listOrdersUseCase: ListOrdersUseCase
    reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase
    pickingFlowUseCase: PickingFlowUseCase
    UpdateOrdersUseCase: UpdateOrdersUseCase
    exportInPickingOrderUseCase: ExportInPickingOrderUseCase
    showOrdersUseCase: ShowOrdersUseCase
    cancelOrderUseCase: CancelOrderUseCase
    importOrderUseCase: ImportOrderUseCase
}

export function useListOrders({ pickingFlowUseCase, listOrdersUseCase, reprocessOrderPaymentsUseCase, UpdateOrdersUseCase, exportInPickingOrderUseCase, showOrdersUseCase, cancelOrderUseCase, importOrderUseCase }: IUseListOrdersProps){
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [pageCancelling, setPageCancelling] = useState(1)
    const [data, setData] = useState<IOrderEntity[]>([])
    const [dataCancelling, setDataCancelling] = useState<IOrderEntity[]>([])
    const [showDetails, setShowDetails] = useState<undefined | string>(undefined)
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [totalRegistersCancelling, setTotalRegistersCancelling] = useState(0)
    const [showModalChoices, setShowModalChoices] = useState(false)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const [currentFilters, setCurrentFilters] = useState<IOrdersListPayload>({ page: 1, store: undefined })
    const [currentFiltersCancelling, setCurrentFiltersCancelling] = useState<IOrdersListPayload>({ page: 1 })
    const [currentFiltersPickingFlow, setCurrentFiltersPickingFlow] = useState<Omit<IOrdersListPayload, 'page'>>({ store: 'B2B' })
    const [orderToReprocess, setOrderToReprocess] = useState<string | undefined>(undefined)
    const [kanbanSchema, setKanbanSchema] = useState<IKanbanBoardOrdersSchemaProps>()
    const [orderToCancel, setOrderToCancel] = useState<IOrderEntity | undefined>(undefined)
    const [showImportOrderModal, setShowImportOrderModal] = useState(false)
    const { user } = useAuth()
    const [value, setValue] = useState(0);
    const [orderDetailsData, setOrderDetailsData] = useState<{
        sale?: ISaleEntity;
        orderData?: IOrderData;
    } | undefined>(undefined);

    const { showToast } =  useToast()

    const hasCancelPermission = useMemo(() => {
      return user?.permissionsScreens?.includes(`feature:orders-cancel:manage`)
    }, [user])
   
    const transformData = (dados: IPickingFlowResponse[]) => {
      console.log("dados: ", dados)
      
      const tasks: Record<string, Task> = {};
      const waitingPicking : any = [];
      const inPicking : any = [];
      const awaitInvoicing : any = [];
      
      dados.forEach((item, index) => {
          // const taskId = `task-${index + 1}`;
          const taskId = item.orderId;
          tasks[taskId] = {
              id: taskId,
              content: {
                  order: `Pedido #${item.nsOrderId}`,
                  customer: `Cliente: ${item.customerName}`,
                  carrier: `Transportadora: ${item.carrierName}`,
                  type: 'click', // Definindo o tipo para renderizar corretamente,
                  observations: item.orderObservations ? `Observações: ${item.orderObservations}` : undefined,

              }
          };

          // Corrigindo a lógica de categorização
          if(item.orderStatus === OrderStatusEnum.AWAITING_PICKING){
            waitingPicking.push(taskId);
          } else if (item.orderStatus === OrderStatusEnum.IN_PICKING){
              inPicking.push(taskId)
          } else if (item.orderStatus === OrderStatusEnum.AWAITING_INVOICING){ 
              awaitInvoicing.push(taskId)
          }
          
      });

      const result = {
          tasks,
          columns: {
            [OrderStatusEnum.AWAITING_PICKING]: {
                  id: OrderStatusEnum.AWAITING_PICKING,
                  title: 'Aguardando Separação',
                  taskIds: waitingPicking,
              },
              [OrderStatusEnum.IN_PICKING]: {
                  id: OrderStatusEnum.IN_PICKING,
                  title: 'Em Separação',
                  taskIds: inPicking,
              },
              [OrderStatusEnum.AWAITING_INVOICING]: {
                  id: OrderStatusEnum.AWAITING_INVOICING,
                  title: 'Aguardando Faturamento',
                  taskIds: awaitInvoicing,
              },
          },
          columnOrder: [OrderStatusEnum.AWAITING_PICKING, OrderStatusEnum.IN_PICKING, OrderStatusEnum.AWAITING_INVOICING],
      };

      return result;
    }

      const handleUpdateOrderStatus = async (payload: { orderId: string, newStatus: OrderStatusEnum }) => {
        try {
          setLoading(true)
          
          const listResult = await UpdateOrdersUseCase.handle({ orderId: payload.orderId, newStatus: payload.newStatus });

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            throw new Error(String(listResult?.error?.error));
          }

          showToast('Status do pedido atualizado com sucesso!', 'success');
        } catch (error) {
          console.log("error handleUpdateOrderStatus function: ", error)
          throw error; // Re-lança o erro para que o componente Kanban saiba que falhou
        } finally {
          setLoading(false);
        }
    };

    const handlePrintPickingList = async () => {
      try {
        setLoading(true)
        const response = await exportInPickingOrderUseCase.handle();

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }
        
        showToast('Lista de separação baixada com sucesso!', 'success');
      } catch (error) {
        console.log("error handlePrintPickingList function: ", error);
        showToast('Erro ao gerar lista de separação.', 'error');
      } finally {
        setLoading(false);
      }
    };

    const handleCardClick = async (orderId: string) => {
      
      try {
        setLoading(true);
        
        const orderResult = await showOrdersUseCase.handle(orderId);

        if (orderResult.isFailure) {
          showToast(String(orderResult?.error?.error), 'error');
          return;
        }

        const result = orderResult.getValue();

        if (result?.id) {
    
          // Transformar os dados do pedido para o formato esperado pela modal
          const saleData: ISaleEntity = {
            id: result.id,
            createdAt: result.createdAt,
            customer: result.customer || {
              id: '',
              name: 'Cliente não informado',
              email: '',
              phone: '',
              personType: 'PF' as const,
              stateRegistration: '',
              document: '',
              addresses: []
            },
            total: result.totalValue,
            shippingAddress: result.shippingAddress || {
              id: '',
              name: '',
              number: '',
              locality: '',
              city: '',
              province: '',
              zipcode: ''
            },
            billingAddress: result.billingAddress || {
              id: '',
              name: '',
              number: '',
              locality: '',
              city: '',
              province: '',
              zipcode: ''
            },
            products: result.products.map((i: any) => {
              const qty = Number(i.totalQuantity) || Number(i.quantity) || 0;
              const unitPrice = Number(i.unitaryPrice) || 0;
              const total = Number(i.finalPrice) || 0;
              return {
                id: i.hubProductId,
                name: i.product?.name ?? 'Produto não informado',
                quantity: qty,
                variant: 'Não informado',
                sku: 'Não informado',
                price_unit: FormatMoney(unitPrice),
                total: FormatMoney(total),
                image: i.product?.images?.[0]?.link || ''
              }
            })
          };

          const orderData: IOrderData = {
            id: result.id,
            nsOrderId: result.nsOrderId || '',
            customer: result.customer ? {
              id: result.customer.id,
              name: result.customer.name,
              email: result.customer.email,
              phone: result.customer.phone || ''
            } : undefined,
            seller: result.seller ? {
              id: result.seller.id,
              name: result.seller.name || ''
            } : undefined,
            sellerAlreadyAdded: !!result.seller,
            observations: result.observations
          };

          setOrderDetailsData({
            sale: saleData,
            orderData: orderData
          });
        }
      } catch (error) {
        console.log("error handleCardClick function: ", error);
        showToast('Erro ao carregar detalhes do pedido.', 'error');
      } finally {
        setLoading(false);
      }
    };

    const listPickingFlow = async (payload: Partial<IOrdersListPayload> = {}) => {
        try {
          setLoading(true)
          
          const { searchBy, status, store, startDate, endDate } = payload;
          
          // Merge current filters with new search/filter parameters
          // Use the provided values if they exist (including empty arrays), otherwise keep current filters
          const filters: Omit<IOrdersListPayload, 'page'> = {
            ...currentFiltersPickingFlow,
          }

          // Apply status if provided (including empty array to clear filters)
          if (status !== undefined) {
            filters.status = status;
          }

          // Apply store: if explicitly passed (even as undefined), apply or remove it
          if ('store' in payload) {
            if (store === null || store === undefined) {
              delete filters.store;
            } else {
              filters.store = store;
            }
          }

          // Apply dates: if explicitly passed (even as undefined), apply or remove them
          if ('startDate' in payload) {
            if (startDate === null || startDate === undefined) {
              delete filters.startDate;
            } else {
              filters.startDate = startDate;
            }
          }

          if ('endDate' in payload) {
            if (endDate === null || endDate === undefined) {
              delete filters.endDate;
            } else {
              filters.endDate = endDate;
            }
          }

          // Add searchBy only if it has a value
          if (searchBy && searchBy.trim() !== '') {
            filters.searchBy = searchBy;
          } else if (searchBy === '' || searchBy === null || searchBy === undefined) {
            delete filters.searchBy;
          }
          
          // Update current filters
          setCurrentFiltersPickingFlow(filters)
          
          const listResult = await pickingFlowUseCase.handle(filters);

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

          const result = listResult.getValue()

          const normalizeSchema : any = transformData(result ?? [])
    
          setKanbanSchema(normalizeSchema)
        } catch (error) {
          console.log("error listSales function: ", error)
        } finally {
          setLoading(false);
        }
    };

    const listOrders = async ({ page: currentPage, searchBy, status, store, startDate, endDate, orderBy }: IOrdersListPayload) => {
        try {
          setLoading(true)
          
          // Merge current filters with new search/filter parameters
          const filters: IOrdersListPayload = {
            ...currentFilters,
            page: currentPage || currentFilters.page,
            status: status || currentFilters.status,
            store,
            startDate: startDate !== undefined ? startDate : currentFilters.startDate,
            endDate: endDate !== undefined ? endDate : currentFilters.endDate,
            orderBy: orderBy !== undefined ? orderBy : currentFilters.orderBy,
          }

          // Add searchBy only if it has a value
          if (searchBy && searchBy.trim() !== '') {
            filters.searchBy = searchBy;
          } else {
            delete filters.searchBy;
          }
          
          // Update current filters
          setCurrentFilters(filters)
          
          const listResult = await listOrdersUseCase.handle(filters);

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

          const list = listResult.getValue()
         
          if(list?.data){
            setData(list.data)
            setTotalRegisters(list.total)
          
          }

          setPage(currentPage)
        } catch (error) {
          console.log("error listSales function: ", error)
        } finally {
          setLoading(false);
        }
    };

    const listOrdersCancelling = async ({ page: currentPage, searchBy, ...rest }: IOrdersListPayload) => {
      try {
        setLoading(true)
        
        // Merge current filters with new search/filter parameters
        const filters: IOrdersListPayload = {
          ...currentFilters,
          page: currentPage || currentFilters.page,
          ...rest,
          status: [OrderStatusEnum.OPEN, OrderStatusEnum.PROCESSING, OrderStatusEnum.AWAITING_PICKING, OrderStatusEnum.IN_PICKING, OrderStatusEnum.AWAITING_INVOICING, OrderStatusEnum.INVOICING, OrderStatusEnum.INVOICE_PROBLEM, OrderStatusEnum.PARTIALLY_INVOICED],
         
          // status: status || currentFilters.status,
        }

        // Add searchBy only if it has a value
        if (searchBy && searchBy.trim() !== '') {
          filters.searchBy = searchBy;
        } else {
          delete filters.searchBy;
        }
        
        // Update current filters
        setCurrentFiltersCancelling(filters)
        
        const listResult = await listOrdersUseCase.handle(filters);

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        const list = listResult.getValue()
       
        if(list?.data){
          setDataCancelling(list.data)
          setTotalRegistersCancelling(list.total)
        
        }

        setPageCancelling(currentPage)
      } catch (error) {
        console.log("error listSales function: ", error)
      } finally {
        setLoading(false);
      }
  };

  const handleSearch = (payload: IOrdersListPayload) => {
    // Extract page from payload for listOrders, but not for listPickingFlow
    const { page } = payload;
    listOrders({ ...currentFilters, ...payload, page: 1 })
    listOrdersCancelling({ ...currentFiltersCancelling, ...payload, page: 1 })
    // Apply search filters to picking flow, explicitly passing store and dates
    listPickingFlow({ 
      store: payload.store,
      startDate: payload.startDate,
      endDate: payload.endDate,
      status: payload.status,
      searchBy: payload.searchBy
    })
  }

    const handleImportOrder = async ({ orderId, store }: IImportOrderPayload) => {
      try {
        setLoading(true)

  
        const response = await importOrderUseCase.handle({ orderId, store });

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }

        showToast('Pedido importado com sucesso', 'success');
        setShowImportOrderModal(false)
      } catch (error) {
        console.log("error listSales function: ", error)
      } finally {
        setLoading(false);
      }
  };



  

    const list = useMemo(() => {
        const rows = data?.map(sale => {
            return {
                ...sale,
                createdAt: format(new Date(sale.createdAt), 'dd/MM/yyyy'),
                products: sale.products.map(product => {
                  return {
                    ...product,
                  }
                })
            }
        })

        return rows
    }, [data])

    console.log("list: ", JSON.stringify(list))

    const listCancelling = useMemo(() => {
      const rows = dataCancelling?.map(sale => {
          return {
              ...sale,
              createdAt: format(new Date(sale.createdAt), 'dd/MM/yyyy'),
              products: sale.products.map(product => {
                return {
                  ...product,
                }
              })
          }
      })

      return rows
  }, [dataCancelling])

    const handleShowDetails = (code: string) => {
      setShowDetails(code)
    }

    const handleFilterSelect = (payload: IOrdersListPayload) => {
      // Extract page from payload for listOrders, but not for listPickingFlow
      const { page, ...pickingFlowPayload } = payload;
      listOrders({ ...currentFilters, ...payload, page: 1 })
      listOrdersCancelling({ ...currentFiltersCancelling, ...payload, page: 1 })
      // Apply filters directly to picking flow, explicitly passing store and dates
      // Using object with explicit properties ensures they are processed even if undefined
      listPickingFlow({ 
        store: payload.store,
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: payload.status,
        searchBy: payload.searchBy
      })
    }

    const handleReprocessPayment = async (orderId: string) => {
        try {
          setLoading(true)
          
        const response =  await reprocessOrderPaymentsUseCase.handle(orderId);

          if (response.isFailure) {
            showToast(String(response?.error?.error), 'error');
            return;
          }

          setOrderToReprocess(undefined)
          showToast('Pagamento reprocessado com sucesso', 'success');
          return;
        } catch (error) {
          console.log("error listSales function: ", error)
        } finally {
          setLoading(false);
        }
    };

    const handleCancelOrder = async (reason?: string) => {
      if(!orderToCancel) return
      try {
        setLoading(true)

        const payload : ICancelOrderPayload = {
          orderId: orderToCancel?.id,
          reason: reason
        }
      
        
      const response =  await cancelOrderUseCase.handle(payload);

        if (response.isFailure) {
          showToast(String(response?.error?.error), 'error');
          return;
        }

        setOrderToCancel(undefined)
        showToast('Pagamento cancelado com sucesso', 'success');
        return;
      } catch (error) {
        console.log("error listSales function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
    };

    function a11yProps(index: number) {
      return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
      };
    }

    // Funções wrapper para manter os filtros na paginação
      const handlePageChangeOrders = (data: IOrdersListPayload) => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        listOrders({ ...currentFilters, ...data })
    }

    const handlePageChangeCancelling = (data: IOrdersListPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
        listOrdersCancelling({ ...currentFiltersCancelling, ...data })
    }

    const handleSortModelChange = (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        const orderBy = {
          field: field,
          order: sort?.toUpperCase() as 'ASC' | 'DESC'
        };
        listOrders({ ...currentFilters, orderBy, page: 1 });
      } else {
        // Se não houver ordenação, remove o orderBy
        const { orderBy, ...filtersWithoutOrder } = currentFilters;
        listOrders({ ...filtersWithoutOrder, page: 1 });
      }
    };

    const handleSortModelChangeCancelling = (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        const orderBy = {
          field: field,
          order: sort?.toUpperCase() as 'ASC' | 'DESC'
        };
        listOrdersCancelling({ ...currentFiltersCancelling, orderBy, page: 1 });
      } else {
        // Se não houver ordenação, remove o orderBy
        const { orderBy, ...filtersWithoutOrder } = currentFiltersCancelling;
        listOrdersCancelling({ ...filtersWithoutOrder, page: 1 });
      }
    };

    useEffect(() => {
      listPickingFlow()
    }, [])

    useEffect(() => {
      listOrders({ page: 1})
      listOrdersCancelling({ page: 1 })
  }, [])


    return {
        loading,
        listOrders,
        list,
        data,
        handleShowDetails,
        showDetails,
        setShowDetails,
        setShowModalChoices,
        showModalChoices,
        totalRegisters,
        setShowFiltersModal,
        showFiltersModal,
        currentFilters,
        setCurrentFilters,
        handleFilterSelect,
        handleReprocessPayment,
        orderToReprocess,
        setOrderToReprocess,
        handleChange,
        a11yProps,
        value,
        kanbanSchema,
        handleUpdateOrderStatus,
        handlePrintPickingList,
        handleCardClick,
        orderDetailsData,
        orderToCancel,
        setOrderToCancel,
        handleCancelOrder,
        hasCancelPermission,
        listCancelling,
        listOrdersCancelling,
        totalRegistersCancelling,
        handleImportOrder,
        showImportOrderModal,
        setShowImportOrderModal,
        currentFiltersCancelling,
        handlePageChangeOrders,
        handlePageChangeCancelling,
        handleSearch,
        handleSortModelChange,
        handleSortModelChangeCancelling
    }
}